<style scoped lang="scss">
.vehicle-group-container {
  width: 100%;
  height: calc(100vh - 100px);
  display: flex;
  background: #f2f2f2;
  padding: 20px;
  box-sizing: border-box;

  // 左侧配置列表
  .config-panel {
    width: 400px;
    background: #ffffff;
    border-radius: 4px;
    padding: 20px;
    margin-right: 20px;
    display: flex;
    flex-direction: column;

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      .panel-title {
        font-size: 16px;
        font-weight: bold;
        color: #333;
      }
    }

    .search-box {
      margin-bottom: 15px;
    }

    .config-list {
      flex: 1;
      overflow-y: auto;

      .config-item {
        padding: 15px;
        margin-bottom: 10px;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        }

        &.active {
          border-color: #409eff;
          background-color: #ecf5ff;
        }

        .config-name {
          font-size: 14px;
          font-weight: bold;
          color: #333;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .status-tag {
            font-size: 12px;
          }
        }

        .config-info {
          font-size: 12px;
          color: #666;
          line-height: 1.8;

          .info-item {
            margin-bottom: 4px;
          }

          .label {
            color: #999;
            margin-right: 5px;
          }
        }

        .config-actions {
          margin-top: 10px;
          text-align: right;

          .el-button {
            padding: 5px 10px;
            font-size: 12px;
          }
        }
      }
    }

    .pagination {
      margin-top: 15px;
      text-align: center;
    }
  }

  // 右侧车辆管理
  .vehicle-panel {
    flex: 1;
    background: #ffffff;
    border-radius: 4px;
    padding: 20px;
    display: flex;
    flex-direction: column;

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      .panel-title {
        font-size: 16px;
        font-weight: bold;
        color: #333;
      }

      .actions {
        display: flex;
        align-items: center;
        gap: 10px;

        .el-button {
          margin-left: 0;
        }
      }
    }

    .vehicle-table {
      flex: 1;
      overflow: hidden;
    }

    .pagination {
      margin-top: 15px;
      text-align: right;
    }
  }

  .empty-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    font-size: 14px;
  }
}
</style>

<template>
  <div class="vehicle-group-container">
    <!-- 左侧配置列表 -->
    <div class="config-panel">
      <div class="panel-header">
        <span class="panel-title">通行组配置</span>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="showAddConfig"
          >新增配置</el-button
        >
      </div>

      <div class="search-box">
        <el-input
          v-model="searchGroupName"
          placeholder="搜索配置名称"
          prefix-icon="el-icon-search"
          clearable
          size="small"
          @clear="fetchConfigList"
          @keyup.enter.native="fetchConfigList"
        >
          <el-button
            slot="append"
            icon="el-icon-search"
            @click="fetchConfigList"
          ></el-button>
        </el-input>
      </div>

      <div class="config-list" v-loading="configLoading">
        <div
          v-for="item in configList"
          :key="item.id"
          class="config-item"
          :class="{ active: selectedConfigId === item.id }"
          @click="selectConfig(item)"
        >
          <div class="config-name">
            <span>{{ item.groupName }}</span>
            <el-tag
              size="mini"
              :type="item.isActive ? 'success' : 'info'"
              class="status-tag"
            >
              {{ item.isActive ? "启用" : "禁用" }}
            </el-tag>
          </div>
          <div class="config-info">
            <div class="info-item">
              <span class="label">免费日期:</span>
              <span>{{ formatWeekDays(item.freeDays) || "无" }}</span>
            </div>
            <div class="info-item">
              <span class="label">收费日期:</span>
              <span>{{ formatWeekDays(item.chargeDays) || "无" }}</span>
            </div>
            <div class="info-item" v-if="item.description">
              <span class="label">描述:</span>
              <span>{{ item.description }}</span>
            </div>
          </div>
          <div class="config-actions">
            <el-button type="text" size="mini" @click.stop="editConfig(item)"
              >编辑</el-button
            >
            <el-button
              type="text"
              size="mini"
              @click.stop="toggleConfigStatus(item)"
            >
              {{ item.isActive ? "禁用" : "启用" }}
            </el-button>
            <el-button
              type="text"
              size="mini"
              style="color: #f56c6c"
              @click.stop="deleteConfig(item)"
              >删除</el-button
            >
          </div>
        </div>

        <el-empty
          v-if="!configLoading && configList.length === 0"
          description="暂无配置数据"
        ></el-empty>
      </div>

      <div class="pagination">
        <el-pagination
          small
          layout="prev, pager, next"
          :current-page="configPage.current"
          :page-size="configPage.size"
          :total="configPage.total"
          @current-change="handleConfigPageChange"
        ></el-pagination>
      </div>
    </div>

    <!-- 右侧车辆管理 -->
    <div class="vehicle-panel">
      <div class="panel-header">
        <span class="panel-title">
          {{
            selectedConfig
              ? `${selectedConfig.groupName} - 车辆管理`
              : "车辆管理"
          }}
        </span>
        <div class="actions" v-if="selectedConfig">
          <el-button
            size="small"
            icon="el-icon-download"
            @click="downloadTemplate"
            >下载模板</el-button
          >
          <el-upload
            action=""
            :show-file-list="false"
            :before-upload="handleImportExcel"
            accept=".csv,.xlsx,.xls"
          >
            <el-button size="small" icon="el-icon-upload2">导入车辆</el-button>
          </el-upload>
          <el-button
            size="small"
            icon="el-icon-plus"
            @click="showBatchAddDialog"
            >批量添加</el-button
          >
          <el-button
            size="small"
            icon="el-icon-delete"
            type="danger"
            @click="batchRemoveVehicles"
            :disabled="selectedVehicles.length === 0"
            >批量移除</el-button
          >
        </div>
      </div>

      <div class="vehicle-table" v-loading="vehicleLoading">
        <el-table
          ref="vehicleTable"
          :data="vehicleList"
          border
          stripe
          height="100%"
          @selection-change="handleSelectionChange"
          v-if="selectedConfig"
        >
          <el-table-column type="selection" width="55"></el-table-column>
          <el-table-column
            prop="carNo"
            label="车牌号"
            min-width="120"
          ></el-table-column>
          <el-table-column
            prop="carOwner"
            label="车主姓名"
            min-width="100"
          ></el-table-column>
          <el-table-column
            prop="phone"
            label="车主手机号"
            min-width="120"
          ></el-table-column>
          <el-table-column
            prop="createTime"
            label="添加时间"
            min-width="160"
          ></el-table-column>
          <el-table-column
            prop="existInSystem"
            label="车辆是否存在于本系统"
            min-width="160"
          >
            <template slot-scope="scope">
              <el-tag
                size="mini"
                :type="scope.row.existInSystem ? 'success' : 'info'"
              >
                {{ scope.row.existInSystem ? "是" : "否" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="small"
                style="color: #f56c6c"
                @click="removeVehicle(scope.row)"
                >移除</el-button
              >
            </template>
          </el-table-column>
        </el-table>

        <div class="empty-tip" v-else>
          <span>请先选择一个通行组配置</span>
        </div>
      </div>

      <div class="pagination" v-if="selectedConfig">
        <el-pagination
          background
          @size-change="handleVehicleSizeChange"
          @current-change="handleVehiclePageChange"
          :current-page="vehiclePage.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="vehiclePage.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="vehiclePage.total"
        ></el-pagination>
      </div>
    </div>

    <!-- 配置表单对话框 -->
    <config-form
      :visible.sync="configFormVisible"
      :config-data="currentConfig"
      @success="handleConfigSuccess"
    ></config-form>

    <!-- 批量添加车辆对话框 -->
    <el-dialog
      title="批量添加车辆"
      :visible.sync="batchAddDialogVisible"
      width="500px"
    >
      <el-form label-width="100px">
        <el-form-item label="车牌号列表">
          <el-input
            type="textarea"
            v-model="batchCarNos"
            :rows="8"
            placeholder="请输入车牌号，每行一个或使用逗号分隔"
          ></el-input>
          <div style="color: #999; font-size: 12px; margin-top: 5px">
            已输入 {{ batchCarNosCount }} 个车牌号
          </div>
          <div style="color: #f12; font-size: 12px; margin-top: 5px">
            车辆添加后将在次日生效！
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="batchAddDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="submitBatchAdd"
          :loading="batchAddLoading"
          >确定添加</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ConfigForm from "./ConfigForm.vue";
import {
  getVehicleGroupConfigPage,
  deleteVehicleGroupConfig,
  activateVehicleGroupConfig,
  deactivateVehicleGroupConfig,
  getVehicleGroupRelationPage,
  removeVehiclesFromGroup,
  batchAddVehiclesToGroup,
  importVehiclesToGroup,
} from "./api";
import { downloadVehicleImportTemplate } from "./utils/template";

export default {
  name: "VehicleGroup",
  components: {
    ConfigForm,
  },
  data() {
    return {
      // 配置相关
      configLoading: false,
      configList: [],
      configPage: {
        current: 1,
        size: 10,
        total: 0,
      },
      searchGroupName: "",

      // 选中的配置
      selectedConfigId: null,
      selectedConfig: null,

      // 配置表单
      configFormVisible: false,
      currentConfig: {},

      // 车辆相关
      vehicleLoading: false,
      vehicleList: [],
      vehiclePage: {
        current: 1,
        size: 10,
        total: 0,
      },
      selectedVehicles: [],

      // 批量添加
      batchAddDialogVisible: false,
      batchCarNos: "",
      batchAddLoading: false,
    };
  },
  computed: {
    batchCarNosCount() {
      if (!this.batchCarNos || this.batchCarNos.trim() === "") {
        return 0;
      }
      const nos = this.batchCarNos
        .split(/[\n,，]/)
        .map((no) => no.trim())
        .filter((no) => no);
      return nos.length;
    },
  },
  mounted() {
    this.fetchConfigList();
  },
  methods: {
    // ==================== 配置管理 ====================

    /**
     * 获取配置列表
     */
    fetchConfigList() {
      this.configLoading = true;
      const params = {
        page: this.configPage.current,
        size: this.configPage.size,
      };
      if (this.searchGroupName) {
        params.groupName = this.searchGroupName;
      }

      getVehicleGroupConfigPage(params)
        .then((res) => {
          if (res.data.code === 20000) {
            this.configList = res.data.data.records || [];
            this.configPage.total = res.data.data.total || 0;
          }
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("获取配置列表失败");
        })
        .finally(() => {
          this.configLoading = false;
        });
    },

    /**
     * 配置分页变化
     */
    handleConfigPageChange(page) {
      this.configPage.current = page;
      this.fetchConfigList();
    },

    /**
     * 选择配置
     */
    selectConfig(config) {
      this.selectedConfigId = config.id;
      this.selectedConfig = config;
      this.vehiclePage.current = 1;
      this.fetchVehicleList();
    },

    /**
     * 显示新增配置对话框
     */
    showAddConfig() {
      this.currentConfig = {};
      this.configFormVisible = true;
    },

    /**
     * 编辑配置
     */
    editConfig(config) {
      this.currentConfig = { ...config };
      this.configFormVisible = true;
    },

    /**
     * 切换配置状态
     */
    toggleConfigStatus(config) {
      const action = config.isActive ? "禁用" : "启用";
      this.$confirm(`确定要${action}该配置吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          const apiCall = config.isActive
            ? deactivateVehicleGroupConfig(config.id)
            : activateVehicleGroupConfig(config.id);

          apiCall
            .then((res) => {
              if (res.data.code === 20000) {
                this.$message.success(`${action}成功`);
                this.fetchConfigList();
                // 如果当前选中的配置被禁用，清空选中状态
                if (config.isActive && this.selectedConfigId === config.id) {
                  this.selectedConfigId = null;
                  this.selectedConfig = null;
                  this.vehicleList = [];
                }
              } else {
                this.$message.error(res.data.message || `${action}失败`);
              }
            })
            .catch((err) => {
              console.error(err);
              this.$message.error(`${action}失败，请稍后重试`);
            });
        })
        .catch(() => {});
    },

    /**
     * 删除配置
     */
    deleteConfig(config) {
      this.$confirm(
        `确定要删除配置"${config.groupName}"吗？删除后不可恢复。`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          deleteVehicleGroupConfig(config.id)
            .then((res) => {
              if (res.data.code === 20000) {
                this.$message.success("删除成功");
                this.fetchConfigList();
                // 如果删除的是当前选中的配置，清空选中状态
                if (this.selectedConfigId === config.id) {
                  this.selectedConfigId = null;
                  this.selectedConfig = null;
                  this.vehicleList = [];
                }
              } else {
                this.$message.error(res.data.message || "删除失败");
              }
            })
            .catch((err) => {
              console.error(err);
              this.$message.error("删除失败，请稍后重试");
            });
        })
        .catch(() => {});
    },

    /**
     * 配置操作成功回调
     */
    handleConfigSuccess() {
      this.fetchConfigList();
    },

    /**
     * 格式化星期几显示
     */
    formatWeekDays(daysStr) {
      if (!daysStr) return "";
      const weekMap = {
        1: "周一",
        2: "周二",
        3: "周三",
        4: "周四",
        5: "周五",
        6: "周六",
        7: "周日",
      };
      return daysStr
        .split(",")
        .map((day) => weekMap[day])
        .join("、");
    },

    // ==================== 车辆管理 ====================

    /**
     * 获取车辆列表
     */
    fetchVehicleList() {
      if (!this.selectedConfigId) return;

      this.vehicleLoading = true;
      const params = {
        page: this.vehiclePage.current,
        size: this.vehiclePage.size,
        groupId: this.selectedConfigId,
      };

      getVehicleGroupRelationPage(params)
        .then((res) => {
          if (res.data.code === 20000) {
            this.vehicleList = res.data.data.records || [];
            this.vehiclePage.total = res.data.data.total || 0;
            this.vehiclePage.current = res.data.data.current || 1;
            this.vehiclePage.size = res.data.data.size || 10;
          }
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("获取车辆列表失败");
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },

    /**
     * 车辆分页大小变化
     */
    handleVehicleSizeChange(size) {
      this.vehiclePage.size = size;
      this.vehiclePage.current = 1;
      this.fetchVehicleList();
    },

    /**
     * 车辆分页变化
     */
    handleVehiclePageChange(page) {
      this.vehiclePage.current = page;
      this.fetchVehicleList();
    },

    /**
     * 表格选择变化
     */
    handleSelectionChange(selection) {
      this.selectedVehicles = selection;
    },

    /**
     * 移除单个车辆
     */
    removeVehicle(vehicle) {
      this.$confirm(`确定要从通行组中移除车辆"${vehicle.carNo}"吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          removeVehiclesFromGroup(this.selectedConfigId, vehicle.carNo)
            .then((res) => {
              if (res.data.code === 20000) {
                this.$message.success("移除成功");
                this.fetchVehicleList();
              } else {
                this.$message.error(res.data.message || "移除失败");
              }
            })
            .catch((err) => {
              console.error(err);
              this.$message.error("移除失败，请稍后重试");
            });
        })
        .catch(() => {});
    },

    /**
     * 批量移除车辆
     */
    batchRemoveVehicles() {
      if (this.selectedVehicles.length === 0) {
        this.$message.warning("请选择要移除的车辆");
        return;
      }

      const carNos = this.selectedVehicles.map((v) => v.carNo).join(",");
      this.$confirm(
        `确定要从通行组中移除选中的 ${this.selectedVehicles.length} 辆车吗？`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          removeVehiclesFromGroup(this.selectedConfigId, carNos)
            .then((res) => {
              if (res.data.code === 20000) {
                this.$message.success("批量移除成功");
                this.fetchVehicleList();
                this.$refs.vehicleTable.clearSelection();
              } else {
                this.$message.error(res.data.message || "批量移除失败");
              }
            })
            .catch((err) => {
              console.error(err);
              this.$message.error("批量移除失败，请稍后重试");
            });
        })
        .catch(() => {});
    },

    /**
     * 显示批量添加对话框
     */
    showBatchAddDialog() {
      this.batchCarNos = "";
      this.batchAddDialogVisible = true;
    },

    /**
     * 提交批量添加
     */
    submitBatchAdd() {
      if (!this.batchCarNos || this.batchCarNos.trim() === "") {
        this.$message.warning("请输入车牌号");
        return;
      }

      // 处理车牌号，支持换行和逗号分隔
      const carNos = this.batchCarNos
        .split(/[\n,，]/)
        .map((no) => no.trim())
        .filter((no) => no);

      if (carNos.length === 0) {
        this.$message.warning("请输入有效的车牌号");
        return;
      }

      this.batchAddLoading = true;
      batchAddVehiclesToGroup(this.selectedConfigId, carNos.join(","))
        .then((res) => {
          if (res.data.code === 20000) {
            this.$message.success(`成功添加 ${carNos.length} 辆车`);
            this.batchAddDialogVisible = false;
            this.fetchVehicleList();
          } else {
            this.$message.error(res.data.message || "批量添加失败");
          }
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("批量添加失败，请稍后重试");
        })
        .finally(() => {
          this.batchAddLoading = false;
        });
    },

    /**
     * 下载导入模板
     */
    downloadTemplate() {
      window.open("/通行组车辆导入模板.xlsx");
    },

    /**
     * 导入Excel/CSV
     */
    handleImportExcel(file) {
      const isValidType =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv";
      if (!isValidType) {
        this.$message.error("只能上传 Excel 或 CSV 文件！");
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.$message.error("上传文件大小不能超过 10MB！");
        return false;
      }

      this.$confirm("确定要导入该文件吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "info",
      })
        .then(() => {
          importVehiclesToGroup(this.selectedConfigId, file)
            .then((res) => {
              if (res.data.code === 20000) {
                this.$message.success("导入成功");
                this.fetchVehicleList();
              } else {
                this.$message.error(res.data.message || "导入失败");
              }
            })
            .catch((err) => {
              console.error(err);
              this.$message.error("导入失败，请检查文件格式是否正确");
            });
        })
        .catch(() => {});

      return false; // 阻止默认上传行为
    },
  },
};
</script>
